import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { useConsent } from "../components/consent/ConsentManager";

const KontaktPage = () => {
  const [contactInfo, setContactInfo] = useState(null);
  const { consent, openConsentSettings } = useConsent();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const res = await axios.get(`${API}/contact-info`);
        setContactInfo(res.data);
      } catch (error) {
        console.error("Error fetching contact info:", error);
      }
    };
    fetchContactInfo();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...formData, subject: "Kontaktanfrage" });
      setSubmitted(true);
      toast.success("Nachricht erfolgreich gesendet!");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Fehler beim Senden der Nachricht. Bitte versuchen Sie es erneut.");
    }
    setIsSubmitting(false);
  };

  return (
    <div data-testid="kontakt-page">
      {/* Hero Section */}
      <section className="relative py-24" style={{ background: 'linear-gradient(135deg, #0A2A43 0%, #0A2A43 50%, #4a1a2a 85%, #6b1f2f 100%)' }}>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 lg:px-16 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-barlow italic animate-on-load animate-fade-in-down">
            Kontakt
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto animate-on-load animate-fade-in-up animation-delay-300">
            Haben Sie Fragen oder möchten Sie ein Projekt besprechen? Wir sind für Sie da!
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-[#0A2A43] mb-6 font-barlow">
                Schreiben Sie uns
              </h2>
              
              {submitted ? (
                <div className="bg-green-50 border border-green-200 rounded-sm p-8 text-center" data-testid="form-success">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-green-800 mb-2">Vielen Dank für Ihre Nachricht!</h3>
                  <p className="text-green-700">Wir werden uns schnellstmöglich bei Ihnen melden.</p>
                  <button 
                    onClick={() => setSubmitted(false)}
                    className="mt-4 text-[#0A2A43] font-semibold hover:text-[#D32F2F]"
                  >
                    Weitere Nachricht senden
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-[#0A2A43] mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        required
                        className="form-input"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        data-testid="contact-form-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#0A2A43] mb-2">
                        E-Mail *
                      </label>
                      <input
                        type="email"
                        required
                        className="form-input"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        data-testid="contact-form-email"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#0A2A43] mb-2">
                      Ihre Nachricht *
                    </label>
                    <textarea
                      required
                      rows={6}
                      className="form-input resize-none"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      data-testid="contact-form-message"
                    />
                  </div>
                  <p className="text-sm text-[#475569] bg-[#F2F2F2] border border-gray-200 rounded-lg p-4" data-testid="contact-form-privacy-note">
                    Ihre Daten werden nur zur Bearbeitung Ihrer Anfrage verwendet. Mehr dazu in unserer <a href="/datenschutz" className="text-[#D32F2F] hover:underline">Datenschutzerklärung</a>.
                  </p>
                  <div className="flex items-start gap-3">
                    <input type="checkbox" required id="privacy" className="mt-1" data-testid="contact-form-privacy" />
                    <label htmlFor="privacy" className="text-sm text-[#475569]">
                      Ich habe die <a href="/datenschutz" className="text-[#D32F2F] hover:underline">Datenschutzerklärung</a> gelesen und stimme der Verarbeitung meiner Daten zu. *
                    </label>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full md:w-auto disabled:opacity-50"
                    data-testid="contact-form-submit"
                  >
                    {isSubmitting ? "Wird gesendet..." : "Nachricht senden"}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold text-[#0A2A43] mb-6 font-barlow">
                Kontaktdaten
              </h2>
              
              {contactInfo && (
                <div className="space-y-6">
                  <div className="bg-[#F2F2F2] p-6 rounded-sm">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#0A2A43] rounded-sm flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-[#0A2A43] mb-1">{contactInfo.company_name}</h3>
                        <p className="text-[#D32F2F] text-sm mb-2">{contactInfo.company_subtitle}</p>
                        <p className="text-[#475569]">{contactInfo.street}</p>
                        <p className="text-[#475569]">{contactInfo.city}</p>
                        <p className="text-[#475569]">{contactInfo.country}</p>
                        <p className="text-[#0A2A43] font-semibold mt-2">Geschäftsführer: {contactInfo.ceo_name || "Petrit Gashi"}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#F2F2F2] p-6 rounded-sm">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#0A2A43] rounded-sm flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-[#0A2A43] mb-2">Telefon</h3>
                        <p className="text-[#475569]">
                          <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="hover:text-[#D32F2F]">
                            Tel: {contactInfo.phone}
                          </a>
                        </p>
                        <p className="text-[#475569]">
                          <a href={`tel:${contactInfo.mobile.replace(/\s/g, '')}`} className="hover:text-[#D32F2F]">
                            Mobil: {contactInfo.mobile}
                          </a>
                        </p>
                        <p className="text-[#475569]">Fax: {contactInfo.fax}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#F2F2F2] p-6 rounded-sm">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#0A2A43] rounded-sm flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-[#0A2A43] mb-2">E-Mail</h3>
                        <a href={`mailto:${contactInfo.email}`} className="text-[#D32F2F] hover:underline">
                          {contactInfo.email}
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#F2F2F2] p-6 rounded-sm">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#0A2A43] rounded-sm flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-[#0A2A43] mb-2">Öffnungszeiten</h3>
                        <p className="text-[#475569]">{contactInfo.opening_hours_weekday}</p>
                        <p className="text-[#475569]">{contactInfo.opening_hours_friday}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-96 bg-gray-200 map-container" data-testid="contact-map-section">
        {consent.marketing ? (
          <iframe
            title="Standort Gashi Bau GmbH"
            src="https://www.google.com/maps?q=Up+de+Heede+33,+49191+Belm,+Germany&output=embed"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="google-map"
          />
        ) : (
          <div className="h-full flex items-center justify-center bg-[#0A2A43] text-white px-6 text-center">
            <div>
              <h3 className="text-2xl font-bold font-barlow mb-3">Karte nur nach Marketing-Einwilligung</h3>
              <p className="text-white/80 max-w-xl mx-auto mb-5">Die eingebettete Google-Maps-Karte wird erst geladen, wenn Sie der Kategorie „Marketing“ zustimmen.</p>
              <button onClick={openConsentSettings} className="px-5 py-3 rounded-lg bg-[#D32F2F] hover:bg-[#D32F2F]/90" data-testid="contact-open-consent-settings">Cookie-Einstellungen öffnen</button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default KontaktPage;
